import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { auth, googleProvider } from "../firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { salvaLog } from "../utils/logger";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

const DOMINIO_AUTORIZZATO = "algores.it";
// Logout forzato: prima il refresh del token Google (silenzioso solo di nome, in realtà apriva
// un popup signInWithPopup ogni 55 minuti) lasciava la sessione viva finché non arrivava una
// modifica con un token scaduto, costringendo l'utente a riautenticarsi a metà lavoro. Ora la
// sessione termina qui, deterministicamente, 55 minuti dopo l'ultimo login riuscito.
const MAX_SESSIONE_MS = 55 * 60 * 1000;

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [accessToken, setAccessToken] = useState(
    () => sessionStorage.getItem("googleAccessToken") || null
  );
  const [loading, setLoading] = useState(true);
  const sessionTimerRef = useRef(null);

  function avviaSessioneTimer(durataMs = MAX_SESSIONE_MS) {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(async () => {
      console.log("Sessione scaduta dopo 55 minuti, logout forzato.");
      await logout();
    }, durataMs);
  }

  async function login() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const email = result.user?.email || "";

      if (!email.endsWith(`@${DOMINIO_AUTORIZZATO}`)) {
        await signOut(auth);
        throw new Error(`Accesso consentito solo agli account @${DOMINIO_AUTORIZZATO}`);
      }

      const token = result._tokenResponse?.oauthAccessToken;
      if (token) {
        setAccessToken(token);
        sessionStorage.setItem("googleAccessToken", token);
        sessionStorage.setItem("loginTimestamp", Date.now().toString());
        avviaSessioneTimer();
      }

      salvaLog(result.user, "Login", "", "", [
        { campo: "Email", da: "—", a: email }
      ]);

      return result;
    } catch (error) {
      console.error("Errore login:", error);
      throw error;
    }
  }

  async function refreshToken() {
    try {
      const loginTimestamp = parseInt(sessionStorage.getItem("loginTimestamp") || "0");
      if (loginTimestamp && Date.now() - loginTimestamp > MAX_SESSIONE_MS) {
        console.log("Sessione scaduta, logout forzato.");
        await logout();
        return null;
      }

      const result = await signInWithPopup(auth, googleProvider);
      const email = result.user?.email || "";

      if (!email.endsWith(`@${DOMINIO_AUTORIZZATO}`)) {
        await signOut(auth);
        return null;
      }

      const token = result._tokenResponse?.oauthAccessToken;
      if (token) {
        setAccessToken(token);
        sessionStorage.setItem("googleAccessToken", token);
        return token;
      }
    } catch (error) {
      console.error("Errore refresh token:", error);
    }
    return null;
  }

  function logout() {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);

    if (currentUser) {
      salvaLog(currentUser, "Logout", "", "", [
        { campo: "Email", da: currentUser.email || "—", a: "—" }
      ]);
    }

    setAccessToken(null);
    setCurrentUser(null);
    sessionStorage.removeItem("googleAccessToken");
    sessionStorage.removeItem("loginTimestamp");
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && !user.email?.endsWith(`@${DOMINIO_AUTORIZZATO}`)) {
        await signOut(auth);
        setCurrentUser(null);
      } else {
        setCurrentUser(user);

        if (user) {
          const loginTimestamp = parseInt(sessionStorage.getItem("loginTimestamp") || "0");
          if (loginTimestamp) {
            const tempoTrascorso = Date.now() - loginTimestamp;
            if (tempoTrascorso > MAX_SESSIONE_MS) {
              await signOut(auth);
              setCurrentUser(null);
              sessionStorage.removeItem("googleAccessToken");
              sessionStorage.removeItem("loginTimestamp");
            } else {
              avviaSessioneTimer(MAX_SESSIONE_MS - tempoTrascorso);
            }
          } else {
            sessionStorage.setItem("loginTimestamp", Date.now().toString());
            avviaSessioneTimer();
          }
        }
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, []);

  const value = { currentUser, accessToken, login, logout, refreshToken };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}